import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { API } from 'api';

import { getPushTokenFromLocalStorage } from 'state/localStorage';
import { persistedQueryClient } from 'components/CacheableQueryClientProvider';
import { WeatherAlerts } from '../../weatherAlerts.types';
import {
  WEATHER_ALERT_CACHE_TTL,
  WEATHER_ALERTS_LIST_CACHE_KEY,
} from '../../weatherAlerts.constants';

type UseWeatherAlertListProps = {
  stationId?: number;
};

type UseWeatherAlertList = Omit<
  UseQueryResult<WeatherAlerts, Error>,
  'data'
> & {
  alerts?: WeatherAlerts;
};

const fetchWeatherAlerts = async (
  pushToken: string,
  stationId?: number,
): Promise<WeatherAlerts> => {
  const response = await API.get<WeatherAlerts>(`weather/alerts/`, {
    params: { pushToken, ...(stationId ? { stationId } : null) },
  });

  return response.data;
};

export const useWeatherAlertList = (
  props: UseWeatherAlertListProps = {},
): UseWeatherAlertList => {
  const { stationId } = props;
  const pushToken = getPushTokenFromLocalStorage();

  const { data, ...results } = useQuery(
    {
      // Include push token in query key so cache is reset when token changes -
      // helpful for when a user toggles notifications permissions on the app
      // or changes subscriptions on another device
      queryKey: [WEATHER_ALERTS_LIST_CACHE_KEY, pushToken, stationId],
      queryFn: () => fetchWeatherAlerts(pushToken!, stationId),
      staleTime: WEATHER_ALERT_CACHE_TTL,
      enabled: Boolean(pushToken),
    },
    persistedQueryClient,
  );

  return {
    ...results,
    alerts: data,
  };
};
